import React, { useState, ReactNode, useEffect, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import SplashBanner from '~/components/common/splash-banner';
import StatusBanner from '~/components/common/status-banner';
import CreateAccountBanner from '~/components/common/create-account-banner';
import {
    GreenBg,
    TrilliumBgContainer,
    InnerContainer,
    MainContent,
    SkipLink,
    TrilliumBgImage,
    TrilliumBgImageStyled,
    VisuallyHiddenElement,
} from '~/components/common/layout/styles';
import Header from '~/components/common/header';
import Footer from '~/components/common/footer';
import Spinner from '~/components/common/spinner';
import {
    isDashboardPage,
    shouldShowStatusBanner,
    shouldShowTrilliumBgContainer,
} from '~/utils/helpers';
import ApplicationHeader from '~/components/common/application-header';
import { imageUrl } from '~/utils/url-helpers';

interface BaseLayoutProps {
    showSplashBanner?: boolean;
    showCreateAccountBanner?: boolean;
    children: ReactNode;
    customAppTitle?: string;
    hideAccountActions?: boolean;
}

const BaseLayout = ({
    showSplashBanner,
    showCreateAccountBanner,
    children,
    customAppTitle,
    hideAccountActions = false,
}: BaseLayoutProps) => {
    const [pageTitle, setPageTitle] = useState('');
    const node = useRef<HTMLElement | null>(null);
    const { t } = useTranslation();
    const router = useRouter();
    const { status } = useSession();

    useEffect(() => {
        /* This is to help with SPA accessibility.
			The user focuses on the <main> element when a new page is loaded, and an aria-live region is announced to the screen reader with the newly laoded page's title. 
		 */
        setPageTitle(document.title);
        const updatePageFocus = () => {
            if (node.current) {
                node.current.focus();
            }
        };

        /* Ensures when a new page is routed to, the window is scrolled to the top */
        const scrollToTop = () => window.scrollTo(0, 0);

        router.events.on('routeChangeStart', updatePageFocus);
        router.events.on('routeChangeComplete', scrollToTop);

        // If the component is unmounted, unsubscribe from the event with the `off` method:
        return () => {
            router.events.off('routeChangeStart', updatePageFocus);
            router.events.off('routeChangeComplete', scrollToTop);
        };
    }, [router.events]);

    useEffect(() => {
        // App state may be stale after a back/forward cache restore, so reload the page.
        const reloadIfRestoredFromCache = (event: PageTransitionEvent) => {
            if (event.persisted) {
                window.location.reload();
            }
        };
        window.addEventListener('pageshow', reloadIfRestoredFromCache);
        return () => window.removeEventListener('pageshow', reloadIfRestoredFromCache);
    }, []);

    if (status === 'loading') {
        return <Spinner isLoading />;
    }

    return (
        <>
            <div>
                <SkipLink as="a" href="#content">
                    {t('base-layout:skipToMainContent.text')}
                </SkipLink>
                <div>
                    <Header />
                    <ApplicationHeader
                        customAppTitle={customAppTitle}
                        hideAccountActions={hideAccountActions}
                    />
                    {shouldShowStatusBanner(router.pathname) && <StatusBanner />}
                    {showSplashBanner && <SplashBanner />}
                    {showCreateAccountBanner && <CreateAccountBanner />}
                    {shouldShowTrilliumBgContainer(router.pathname) && (
                        <TrilliumBgContainer $path={router.pathname}>
                            <TrilliumBgImageStyled
                                width={300}
                                height={245}
                                src={imageUrl(
                                    router.pathname === '/products/photo-card'
                                        ? 'images/ontario-icon-trillium-purple.png'
                                        : 'images/ontario-icon-trillium-blue.png'
                                )}
                                alt="Trillium Logo"
                                priority
                            />{' '}
                        </TrilliumBgContainer>
                    )}
                </div>
                <div>
                    {!!isDashboardPage(router.pathname) && (
                        <GreenBg>
                            <TrilliumBgImage
                                width={500}
                                height={300}
                                src={imageUrl('images/trillium-logo-bg.png')}
                                alt="Trillium Logo"
                            />{' '}
                        </GreenBg>
                    )}

                    <MainContent
                        id="content"
                        role="main"
                        className="ontario-row"
                        ref={node}
                        $isDashboard={isDashboardPage(router.pathname)}
                    >
                        <VisuallyHiddenElement
                            role="log"
                            aria-live="assertive"
                            aria-relevant="additions"
                            aria-atomic="true"
                        >
                            {t('base-layout:accessibilityBlock.text')} {pageTitle}
                        </VisuallyHiddenElement>
                        <InnerContainer>{children}</InnerContainer>
                    </MainContent>
                </div>
                <Footer role="contentinfo" />
            </div>
        </>
    );
};

export default BaseLayout;
